import styled from 'styled-components';
import { color, duration, ease, font, radius, shadow } from '../../../theme/tokens';
import { floaty, reducedMotion } from '../../atome/motion/motion.styled';

export const FounderSection = styled.section`
  position: relative;
  padding: 5rem 0;
  background: linear-gradient(180deg, ${color.cream} 0%, ${color.sandLight} 100%);
  overflow: hidden;
`;

/** Oversized watermark word behind the portrait — the editorial anchor. */
export const Watermark = styled.span`
  position: absolute;
  top: 6%;
  left: -2%;
  font-family: ${font.display};
  font-size: clamp(6rem, 18vw, 15rem);
  line-height: 1;
  font-weight: 600;
  color: rgba(74, 112, 97, 0.06);
  letter-spacing: -0.02em;
  pointer-events: none;
  user-select: none;
`;

export const PortraitStack = styled.div`
  position: relative;
  padding: 0 0 3.5rem 2.2rem;

  @media (max-width: 900px) {
    padding: 0 0 3rem 1.4rem;
  }
`;

/** Sand block offset behind the photo. */
export const PortraitBacking = styled.div`
  position: absolute;
  top: -1.6rem;
  left: 0;
  width: 72%;
  height: 78%;
  border-radius: ${radius.lg};
  background: ${color.sand};
  opacity: 0.85;
`;

export const PortraitFrame = styled.div`
  position: relative;
  border-radius: ${radius.lg};
  overflow: hidden;
  box-shadow: ${shadow.lift};
  aspect-ratio: 4 / 5;
  background: ${color.creamDeep};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    transform: scale(1.02);
    transition: transform 1.2s ${ease.out}, filter ${duration.slow} ${ease.out};
  }

  &:hover img {
    transform: scale(1.08);
  }

  /* warm wash so the portrait sits inside the palette */
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(226, 213, 196, 0) 55%, rgba(74, 112, 97, 0.22) 100%);
    pointer-events: none;
  }

  ${reducedMotion};
`;

/** The small product shot that overlaps the portrait's lower corner. */
export const PortraitInset = styled.div`
  position: absolute;
  right: -0.5rem;
  bottom: 0;
  width: 44%;
  max-width: 210px;
  aspect-ratio: 1 / 1;
  border-radius: ${radius.md};
  overflow: hidden;
  border: 6px solid ${color.cream};
  box-shadow: ${shadow.soft};
  animation: ${floaty} 7s ${ease.inOut} infinite;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  ${reducedMotion};
`;

export const Quote = styled.blockquote`
  position: relative;
  margin: 1.6rem 0 1.8rem;
  padding-left: 1.6rem;
  border-left: 2px solid ${color.sandDeep};
  font-family: ${font.display};
  font-size: clamp(1.25rem, 2.4vw, 1.7rem);
  line-height: 1.5;
  color: ${color.ink};
  font-style: italic;

  &::before {
    content: '“';
    position: absolute;
    top: -1.4rem;
    left: 0.6rem;
    font-size: 4rem;
    color: ${color.sandDeep};
    opacity: 0.55;
    line-height: 1;
  }
`;

export const FounderCopy = styled.div`
  h2 {
    font-family: ${font.display};
    font-size: clamp(2rem, 4vw, 3rem);
    font-weight: 600;
    line-height: 1.1;
    color: ${color.ink};
    margin: 0.8rem 0 0;
  }

  p {
    font-family: ${font.body};
    color: ${color.inkSoft};
    line-height: 1.8;
    font-size: 0.98rem;
    margin: 0 0 1rem;
  }
`;

export const Signature = styled.div`
  display: flex;
  align-items: center;
  gap: 0.9rem;
  margin-top: 1.6rem;
  padding-top: 1.4rem;
  border-top: 1px solid ${color.line};

  strong {
    display: block;
    font-family: ${font.display};
    font-size: 1.35rem;
    font-weight: 600;
    color: ${color.ink};
  }

  span {
    font-family: ${font.body};
    font-size: 0.78rem;
    letter-spacing: 0.18em;
    text-transform: uppercase;
    color: ${color.forest};
  }
`;

export const SignatureMark = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: ${color.forest};
  color: #fff;
  font-family: ${font.display};
  font-size: 1.4rem;
  font-weight: 600;
  box-shadow: 0 10px 22px rgba(74, 112, 97, 0.3);
`;

export const Pillars = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1.2rem;
  margin-top: 2.2rem;

  div {
    padding: 1rem 1.1rem;
    border-radius: ${radius.md};
    background: rgba(255, 255, 255, 0.7);
    border: 1px solid rgba(255, 255, 255, 0.9);
    transition: transform ${duration.base} ${ease.out}, box-shadow ${duration.base} ${ease.out};
  }

  div:hover {
    transform: translateY(-5px);
    box-shadow: ${shadow.soft};
  }

  strong {
    display: block;
    font-family: ${font.display};
    font-size: 1.5rem;
    color: ${color.forest};
  }

  span {
    font-family: ${font.body};
    font-size: 0.78rem;
    color: ${color.inkSoft};
  }

  ${reducedMotion};
`;
